import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  TextField,
  makeStyles
} from "@material-ui/core";
import clsx from "clsx";
import AlarmRuleList, {
  useAlarmRuleList
} from "@/components/FormFields/AlarmRuleList";
import SingleSelector from "@/components/SingleSelector";
import { useTranslation } from "@/langs/useTranslation";
import { IconSelect, IconTrash } from "@/components/Icon";
import { uuid } from "@/helpers/utils";
import AppModal from "./AppModal";

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: 4,
    overflow: "hidden",
    border: "1px solid #E9E8ED",
    marginTop: theme.spacing(2)
  },
  header: {
    backgroundColor: "#F8F9FC"
  },
  item: {
    display: "flex",
    alignItems: "center",

    "&:not(:last-child)": {
      borderBottom: "1px solid #E9E8ED"
    },
    "& > div": {
      padding: theme.spacing(1),
      "& > div": {
        margin: 0
      }
    },
    "& > div:nth-child(1)": {
      flex: 1
    },
    "& > div:nth-child(2)": {
      flex: 0.5
    },
    "& > div:nth-child(3)": {
      flex: 1.2
    },
    "& > div:nth-child(4)": {
      flex: 0.5
    },
    "& > div:nth-child(5)": {
      flex: 1
    }
  }
}));

const RULE_VALUES = {
  "CPU Load": [60, 70, 80, 90],
  "RAM Load": [60, 70, 80, 90],
  "CPU Temperature": [70, 75, 80, 85, 90],
  "Storage Usage": [60, 70, 80, 90],
  "Networking Signal Level": [-70, -85, -95, -105],
  "Networking Throughput": [-70, -85, -95, -105],
  "Peripheral Monitor": [
    { id: "USB\\VID_04B8&PID_0E28", name: "EPSON TM-T82III Receipt" },
    { id: "USB\\VID_04B8&PID_0E20", name: "EPSON TM-m30" },
    { id: "USB\\VID_04B8&PID_0202", name: "TM-T88V" }
  ]
};

const RuleHeader = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <div className={clsx(classes.item, classes.header)}>
      <div>{t({ defaultMessage: "规则名称" })}</div>
      <div>{t({ defaultMessage: "条件" })}</div>
      <div>{t({ defaultMessage: "报警触发值" })}</div>
      <div>{t({ defaultMessage: "自动重启" })}</div>
      <div>{t({ defaultMessage: "通知上限次数" })}</div>
    </div>
  );
};

const RuleItem = ({
  index,
  excludes,
  disabled,
  alarm_policy_option_id,
  operator,
  value,
  ext = {},
  unit,
  name,
  notice_upper_limit,
  onChange,
  onDelete
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const alramRuleList = useAlarmRuleList();
  const isOptionOfflineMonitor = name === "Device Offline Monitor";
  const isPeripheralMonitor = name === "Peripheral Monitor";
  const isAppCrashDetection =
    alarm_policy_option_id && name === "App Crash Detection";
  const [appModalOpen, setAppModalOpen] = useState(false);

  const valueOptions = useMemo(() => {
    const target = alramRuleList.find(
      item => item.id === alarm_policy_option_id
    );
    if (target) {
      const options = RULE_VALUES[target.name] ?? [];
      if (typeof options[0] === "object" && options[0].id) {
        return options;
      }
      return options.map(item => ({ id: item, name: item }));
    }
    return [];
  }, [alarm_policy_option_id, alramRuleList]);

  return (
    <div className={classes.item}>
      <div>
        <AlarmRuleList
          excludes={excludes.filter(item => item !== alarm_policy_option_id)} // 要包含自己
          disabled={disabled}
          value={alarm_policy_option_id}
          onChange={(e, option) => {
            onChange(
              e.target.value,
              option.unit,
              option.name,
              "alarm_policy_option_id",
              index
            );
          }}
        />
      </div>
      <div>
        {(isAppCrashDetection || isPeripheralMonitor) && "="}
        {!isOptionOfflineMonitor &&
          !isAppCrashDetection &&
          !isPeripheralMonitor && (
            <SingleSelector
              disabled={disabled}
              value={operator}
              options={[
                { id: ">", name: ">" },
                // { id: "<", name: "<" },
                { id: ">=", name: ">=" }
                // { id: "<=", name: "<=" }
              ]}
              onChange={e => {
                onChange(e.target.value, "", "", "operator", index);
              }}
            />
          )}
      </div>
      <div>
        {isAppCrashDetection && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                // readOnly: true,
                maxLength: 100
              }}
              placeholder={t({ defaultMessage: "请选择应用" })}
              value={ext.app}
              disabled={disabled}
              onChange={e => {
                const { value } = e.target;
                onChange(value, "", "", "app", index, { app: value });
              }}
            />
            <IconSelect
              style={{ marginLeft: 8 }}
              onClick={() => {
                if (!disabled) setAppModalOpen(true);
              }}
            />
          </div>
        )}
        {!isOptionOfflineMonitor && !isAppCrashDetection && (
          <>
            <SingleSelector
              placeholder={t({ defaultMessage: "选择报警触发值" })}
              disabled={disabled}
              value={isPeripheralMonitor ? ext.value : value}
              options={valueOptions}
              onChange={e => {
                const { value } = e.target;
                if (isPeripheralMonitor) {
                  onChange(value, "", "", "value", index, {
                    name: valueOptions.find(item => item.id === value)?.name,
                    value
                  });
                } else {
                  onChange(value, "", "", "value", index);
                }
              }}
            />
            <span style={{ marginLeft: 12 }}>{unit}</span>
          </>
        )}
      </div>
      <div>
        {isAppCrashDetection && (
          <Checkbox
            checked={!!ext.auto_restart}
            onChange={e => {
              const value = e.target.checked ? 1 : 0;
              onChange(value, "", "", "auto_restart", index, {
                auto_restart: value
              });
            }}
          />
        )}
      </div>
      <div style={{ display: "flex" }}>
        <TextField
          variant="outlined"
          size="small"
          disabled={disabled}
          placeholder={t({ defaultMessage: "请填写通知上限次数" })}
          value={notice_upper_limit}
          onChange={e => {
            const { value } = e.target;
            const num = value === "" ? value : parseInt(value) || 1;
            onChange(num, "", "", "notice_upper_limit", index);
          }}
        />

        {!disabled && (
          <IconButton
            style={{ float: "right" }}
            onClick={() => onDelete(index)}
          >
            <IconTrash color="secondary" size={14} />
          </IconButton>
        )}
      </div>
      <AppModal
        open={appModalOpen}
        onClose={() => setAppModalOpen(false)}
        onChange={onChange}
        index={index}
      />
    </div>
  );
};

const AlarmRuleCreator = ({ disabled, name, value = [], onChange }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const rules = useAlarmRuleList();

  const excludeIds = useMemo(() => {
    const appCrashDetectionId = (
      rules.find(v => v.name === "App Crash Detection") || {}
    ).id;
    const peripheralMonitorId = (
      rules.find(v => v.name === "Peripheral Monitor") || {}
    ).id;
    return value
      .filter(
        v =>
          v.alarm_policy_option_id !== appCrashDetectionId &&
          v.alarm_policy_option_id !== peripheralMonitorId
      )
      .map(item => item.alarm_policy_option_id);
  }, [value, rules]);

  const changeHandler = value => {
    onChange({ target: { name, value } });
  };

  const add = () => {
    changeHandler([
      ...value,
      {
        id: uuid(),
        alarm_policy_option_id: "",
        operator: ">",
        value: "",
        unit: "",
        notice_upper_limit: "",
        ext: {}
      }
    ]);
  };

  const handleChange = (fieldValue, unit, name, field, index, ext) => {
    changeHandler(
      value.map((item, i) => {
        // kiosk模式下多个app重启有问题，所以这里改成所有app重启变成唯一单选。
        if (
          ext &&
          field === "auto_restart" &&
          fieldValue === 1 &&
          i !== index
        ) {
          return {
            ...item,
            ext: { ...item.ext, auto_restart: 0 }
          };
        }
        if (i === index) {
          if (ext) {
            return {
              ...item,
              value: 0,
              ext: { ...item.ext, ...ext },
              unit: unit || item.unit,
              name: name || item.name
            };
          }
          return {
            ...item,
            value: name === "Device Offline Monitor" ? 0 : item.value,
            ext:
              name === "App Crash Detection"
                ? { app: "", auto_restart: 0 }
                : item.ext,
            [field]: fieldValue,
            operator:
              name === "Peripheral Monitor" || name === "App Crash Detection"
                ? "="
                : item.operator,
            unit: unit || item.unit,
            name: name || item.name
          };
        }
        return item;
      })
    );
  };

  const handleDelete = index => {
    changeHandler(value.filter((item, i) => i !== index));
  };

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        disabled={disabled}
        onClick={add}
      >
        {t({ defaultMessage: "添加报警规则" })}
      </Button>
      <div className={classes.root}>
        <RuleHeader />
        {value.map((item, index) => {
          return (
            <RuleItem
              key={item.id}
              excludes={excludeIds}
              disabled={disabled}
              index={index}
              alarm_policy_option_id={item.alarm_policy_option_id}
              operator={item.operator}
              value={item.value}
              unit={item.unit}
              name={item.name || item.alarm_policy_option?.name}
              ext={item.ext}
              notice_upper_limit={item.notice_upper_limit}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          );
        })}
      </div>
    </>
  );
};

export default AlarmRuleCreator;
